import React, { useState, useEffect } from 'react';
import api from '../services/api';
import SessionHistory from '../components/SessionHistory';
import './TherapiePage.css';

// Delete Account Component
function DeleteAccount() {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDeleteAccount = async () => {
    // First confirmation
    if (!window.confirm(
      '⚠️ ATTENTION - Cette action est IRRÉVERSIBLE ⚠️\n\n' +
      'Êtes-vous ABSOLUMENT certain de vouloir supprimer votre compte ?\n\n' +
      'Cela va supprimer définitivement :\n' +
      '• Toutes vos sessions de chat\n' +
      '• Tous vos messages\n' +
      '• Votre profil patient\n' +
      '• Votre compte utilisateur\n\n' +
      'Cette action ne peut pas être annulée !'
    )) {
      return;
    }

    // Second confirmation - must type to confirm
    const confirmation = window.prompt(
      'Pour confirmer, tapez "SUPPRIMER" en majuscules :'
    );

    if (confirmation !== 'SUPPRIMER') {
      alert('Suppression annulée.');
      return;
    }

    // Third and final confirmation
    if (!window.confirm(
      'Dernière chance !\n\n' +
      'Toutes vos données vont être perdues définitivement.\n' +
      'Voulez-vous vraiment continuer ?'
    )) {
      return;
    }

    setDeleting(true);
    setError('');

    try {
      await api.deleteAccount();

      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('chat_messages');
      localStorage.removeItem('chat_session_id');

      // Show success message and redirect
      alert(
        '✅ Compte supprimé avec succès !\n\n' +
        'Toutes vos données ont été définitivement supprimées.\n\n' +
        'Redirection vers la page d\'accueil...'
      );

      // Redirect to home
      window.location.href = '/chat';
    } catch (err) {
      console.error('Delete account error:', err);
      setError('Erreur lors de la suppression du compte : ' + (err.response?.data?.detail || err.message));
      setDeleting(false);
    }
  };

  return (
    <div className="delete-account">
      <div className="delete-account-header">
        <h3>Suppression du Compte</h3>
        <p className="warning-text">
          ⚠️ Attention : Cette action est irréversible
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="delete-account-content">
        <div className="delete-account-info">
          <h4>Ce qui va être supprimé :</h4>
          <ul>
            <li>✓ Toutes vos sessions de chat</li>
            <li>✓ Tous vos messages (questions et réponses)</li>
            <li>✓ Votre profil patient complet</li>
            <li>✓ Votre compte utilisateur</li>
            <li>✓ Tous les résumés de sessions</li>
          </ul>

          <div className="delete-account-warning">
            <p><strong>⚠️ IMPORTANT :</strong></p>
            <p>
              Une fois le compte supprimé, il sera impossible de récupérer vos données.
              Nous vous conseillons de télécharger vos données importantes avant de procéder.
            </p>
          </div>
        </div>

        <div className="delete-account-actions">
          <button
            onClick={handleDeleteAccount}
            disabled={deleting}
            className="btn-danger btn-large"
          >
            {deleting ? 'Suppression en cours...' : '🗑️ Supprimer mon compte définitivement'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TherapiePage() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionSummary, setSessionSummary] = useState(null);
  const [patientProfile, setPatientProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingProfile, setGeneratingProfile] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('sessions'); // 'sessions', 'profile', or 'delete'

  useEffect(() => {
    loadSessions();
    loadProfile();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const data = await api.getSessions();
      setSessions(data);
    } catch (err) {
      setError('Erreur lors du chargement des sessions');
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const data = await api.getProfile();
      setPatientProfile(data);
    } catch (err) {
      console.error('Erreur lors du chargement du profil:', err);
    }
  };

  const handleSessionSelect = async (sessionId) => {
    try {
      const session = await api.getSession(sessionId);
      setSelectedSession(session);

      // Load session summary if available
      try {
        const summary = await api.getSessionSummary(sessionId);
        setSessionSummary(summary.summary || null);
      } catch (err) {
        setSessionSummary(null);
      }
    } catch (err) {
      setError('Erreur lors du chargement de la session');
    }
  };

  const handleBack = () => {
    setSelectedSession(null);
    setSessionSummary(null);
  };

  const handleEndSession = async (sessionId) => {
    try {
      const result = await api.endSession(sessionId);
      setSessionSummary(result.summary);

      // Reload sessions to update the list
      await loadSessions();

      // Update the selected session to show it's ended
      if (selectedSession) {
        const updatedSession = await api.getSession(sessionId);
        setSelectedSession(updatedSession);
      }

      // Reload profile as it might have been updated
      await loadProfile();

      alert('Session terminée et résumé généré avec succès!');
    } catch (err) {
      setError('Erreur lors de la fin de session: ' + err.message);
    }
  };

  const handleGenerateProfile = async () => {
    if (!window.confirm(
      'Générer le profil patient à partir de toutes les sessions terminées?\n\n' +
      'Seules les sessions avec un résumé seront utilisées.'
    )) {
      return;
    }

    setGeneratingProfile(true);
    setError('');
    try {
      const updatedProfile = await api.generateProfile();
      setPatientProfile(updatedProfile);
      alert('Profil patient généré avec succès!');
    } catch (err) {
      setError('Erreur lors de la génération du profil: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGeneratingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="therapy-page">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  // Session detail view
  if (selectedSession) {
    return (
      <div className="therapy-page">
        {error && <div className="error-message">{error}</div>}

        <div className="session-detail">
          <button onClick={handleBack} className="btn-back">
            &larr; Retour à l'historique
          </button>

          <div className="session-header">
            <div>
              <h3>Session du {new Date(selectedSession.started_at).toLocaleDateString('fr-FR')}</h3>
              <span className="session-time">
                {new Date(selectedSession.started_at).toLocaleTimeString('fr-FR')}
                {selectedSession.ended_at && ` - ${new Date(selectedSession.ended_at).toLocaleTimeString('fr-FR')}`}
              </span>
            </div>
            {!selectedSession.ended_at && (
              <button
                onClick={() => handleEndSession(selectedSession.id)}
                className="btn-end-session"
              >
                Terminer la session
              </button>
            )}
          </div>

          {sessionSummary && (
            <div className="session-summary">
              <h4>Résumé de la séance</h4>
              <p>{sessionSummary}</p>
            </div>
          )}

          <div className="messages-list">
            {selectedSession.messages && selectedSession.messages.length > 0 ? (
              selectedSession.messages.map((msg) => (
                <div key={msg.id} className={`message ${msg.role}`}>
                  <div className="message-header">
                    <span className="message-role">
                      {msg.role === 'user' ? 'Vous' : 'Assistant'}
                    </span>
                    <span className="message-time">
                      {new Date(msg.timestamp).toLocaleTimeString('fr-FR')}
                    </span>
                  </div>
                  <div className="message-content">{msg.content}</div>
                </div>
              ))
            ) : (
              <p className="empty-messages">Aucun message dans cette session</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main view with tabs
  return (
    <div className="therapy-page">
      {error && <div className="error-message">{error}</div>}

      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          Historique des Sessions
        </button>
        <button
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profil Patient
        </button>
        <button
          className={`tab-button tab-button-danger ${activeTab === 'delete' ? 'active' : ''}`}
          onClick={() => setActiveTab('delete')}
        >
          Suppression du compte
        </button>
      </div>

      {activeTab === 'sessions' ? (
        <SessionHistory
          sessions={sessions}
          onSessionSelect={handleSessionSelect}
        />
      ) : activeTab === 'profile' ? (
        <PatientProfile
          profile={patientProfile}
          sessions={sessions}
          onGenerateProfile={handleGenerateProfile}
          generatingProfile={generatingProfile}
          onSessionSelect={handleSessionSelect}
        />
      ) : (
        <DeleteAccount />
      )}
    </div>
  );
}

// Patient Profile Component
function PatientProfile({ profile, sessions = [], onGenerateProfile, generatingProfile, onSessionSelect }) {
  if (!profile) {
    return (
      <div className="patient-profile">
        <div className="loading">Chargement du profil...</div>
      </div>
    );
  }

  // Count sessions with summaries
  const sessionsWithSummaries = sessions.filter(s => s.summary).length;
  const hasProfileData = profile.history || profile.symptoms || profile.state ||
    profile.diagnosis || profile.goals || profile.progress_notes;

  return (
    <div className="patient-profile">
      <div className="profile-header">
        <h3>Profil Patient</h3>
        {sessionsWithSummaries > 0 && !hasProfileData && (
          <button
            onClick={onGenerateProfile}
            disabled={generatingProfile}
            className="btn-generate-profile"
          >
            {generatingProfile ? 'Génération...' : 'Générer le profil'}
          </button>
        )}
      </div>

      <div className="profile-sections">
        {hasProfileData ? (
          <>
            {profile.history && (
              <div className="profile-section">
                <h4>Histoire</h4>
                <p>{profile.history}</p>
              </div>
            )}

            {profile.symptoms && (
              <div className="profile-section">
                <h4>Symptômes</h4>
                <p>{profile.symptoms}</p>
              </div>
            )}

            {profile.state && (
              <div className="profile-section">
                <h4>État Actuel</h4>
                <p>{profile.state}</p>
              </div>
            )}

            {profile.diagnosis && (
              <div className="profile-section">
                <h4>Diagnostic</h4>
                <p>{profile.diagnosis}</p>
              </div>
            )}

            {profile.goals && (
              <div className="profile-section">
                <h4>Objectifs</h4>
                <p>{profile.goals}</p>
              </div>
            )}

            {profile.progress_notes && (
              <div className="profile-section">
                <h4>Notes de Progression</h4>
                <p>{profile.progress_notes}</p>
              </div>
            )}
          </>
        ) : (
          <div className="profile-empty">
            <p className="empty-profile">
              Aucune information de profil enregistrée.
            </p>
            {sessionsWithSummaries > 0 ? (
              <p className="profile-hint">
                {sessionsWithSummaries} session(s) avec résumé disponible(s).
                Cliquez sur "Générer le profil" pour créer un profil à partir de vos sessions.
              </p>
            ) : sessions.length > 0 ? (
              <p className="profile-hint">
                {sessions.length} session(s) disponible(s) mais aucune n'a été terminée.
                Terminez une session pour générer un résumé et mettre à jour le profil.
              </p>
            ) : (
              <p className="profile-hint">
                Le profil sera rempli automatiquement après vos premières sessions.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Show session summaries section */}
      {sessions.filter(s => s.summary).length > 0 && (
        <div className="profile-sessions">
          <h4>Résumés des Sessions</h4>
          <div className="session-summaries-list">
            {sessions
              .filter(s => s.summary)
              .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
              .map((session) => (
                <div key={session.id} className="session-summary-item">
                  <div className="session-summary-header">
                    <span className="session-date">
                      {new Date(session.started_at).toLocaleDateString('fr-FR')} à{' '}
                      {new Date(session.started_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <button
                      className="btn-view-session"
                      onClick={() => onSessionSelect(session.id)}
                    >
                      Voir détails
                    </button>
                  </div>
                  <p className="session-summary-text">{session.summary}</p>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="profile-meta">
        <small>
          Profil créé le {new Date(profile.created_at).toLocaleDateString('fr-FR')}
          {profile.updated_at && ` • Mis à jour le ${new Date(profile.updated_at).toLocaleDateString('fr-FR')}`}
        </small>
      </div>
    </div>
  );
}

export default TherapiePage;
